import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { ColDef } from 'ag-grid-enterprise';

import { buildColDef, ColType } from '@app/shared/utils/ag-grid.col-def.utils';
import { AgGridConstants } from 'v2/modules/shared/constants/ag-grid.constants';
import { DecimalPlacesUtils } from 'v2/modules/shared/util/decimal-places.utils';

import { AgGridCaretCellRendererComponent } from '../../ag-grid-caret-cell-renderer/ag-grid-caret-cell-renderer.component';
import { IAgGridRouterLinkCellParams } from '../../ag-grid-router-link-cell/ag-grid-router-link-cell.component';

const COL_DEFS :ColDef[] = [
  {
    headerCheckboxSelection: true,
    checkboxSelection: true,
    filter: false,
    filterParams: null,
    floatingFilter: false,
    lockPinned: true,
    lockPosition: true,
    resizable: false,
    sortable: false,
    suppressColumnsToolPanel: true,
    suppressMenu: true,
    suppressMovable: true,
    suppressNavigable: true,
    width: 40,
    maxWidth: 40,
    minWidth: 40,
    pinned: 'left' as const,
  },
  {
    headerName: 'Order #',
    field: 'orderNumber',
    sortable: true,
    filter: true,
    resizable: true,
    width: 150,
    cellRenderer: 'caretRenderer', // Use the custom caret renderer
    cellRendererParams: (params) => ({
      showCaret: params.data?.boms?.length ? true : false, // Dynamic parameter based on row data
    }),
  },
  buildColDef(
    ColType.NULL,
    {
      headerName: 'SKU',
      field: 'sku',
      sortable: true,
      filter: true,
      resizable: true,
      width: 150,
    },
    {
      routerLinkFormatter: ({ data } :IAgGridRouterLinkCellParams) =>
        `/inventory/${data.rfqId}/view`,
    },
  ),
  buildColDef(
    ColType.NULL,
    {
      headerName: 'Product Name',
      field: 'name',
      sortable: true,
      filter: true,
      resizable: true,
      width: 200,
    },
    {
      routerLinkFormatter: ({ data } :IAgGridRouterLinkCellParams) =>
        `/inventory/${data.rfqId}/view`,
    },
  ),
  {
    headerName: 'Qty',
    field: 'quantity',
    sortable: true,
    filter: 'agNumberColumnFilter', // Number filter
    resizable: true,
    width: 150,
    cellRenderer: (params) => DecimalPlacesUtils.format(params.value),
  },
  {
    headerName: 'UOM',
    field: 'uom',
    sortable: true,
    filter: true,
    resizable: true,
    width: 100,
  },
  {
    headerName: 'Avail. Stock',
    field: 'stocks',
    sortable: true,
    filter: true,
    resizable: true,
    width: 150,
    cellRenderer: (params) => DecimalPlacesUtils.format(params.value),
  },
  {
    headerName: 'Line Item Tags',
    field: 'tag',
    sortable: true,
    resizable: true,
    width: 150,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      if (Array.isArray(params.value) && params.value.length > 0) {
        return params.value
          .map((tag :string) => {
            return `<span class="lum-chip mr-2">${tag}</span>`;
          })
          .join('');
      }

      return '';
    },
    tooltipValueGetter: (params) => {
      if (Array.isArray(params.value) && params.value.length > 0) {
        return params.value.join(', '); // Show all hidden tags in tooltip
      }

      return '';
    },
    tooltipComponentParams: {
      placement: 'top',
    },
  },
  {
    headerName: 'Product Tags',
    field: 'rfqTag',
    sortable: true,
    resizable: true,
    width: 150,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      if (Array.isArray(params.value) && params.value.length > 0) {
        return params.value
          .map((tag :string) => {
            return `<span class="lum-chip mr-2">${tag}</span>`;
          })
          .join('');
      }

      return '';
    },
    tooltipValueGetter: (params) => {
      if (Array.isArray(params.value) && params.value.length > 0) {
        return params.value.join(', '); // Show all hidden tags in tooltip
      }

      return '';
    },
    tooltipComponentParams: {
      placement: 'top',
    },
  },
  {
    headerName: 'Preferred Supplier',
    field: 'supplier',
    sortable: true,
    resizable: true,
    width: 200,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
];

const gridOptionsFactory = (params :{
  columnDefs :ColDef[];
  context :{ [key :string] :any };
  getDetailRowData :(params :any) => void;
  onDetailGridReady :(event :GridReadyEvent) => void;
}) :GridOptions => ({
  alwaysShowVerticalScroll: true,
  columnDefs: [...params.columnDefs],
  components: {
    caretRenderer: AgGridCaretCellRendererComponent,
  },
  context: params.context,
  defaultColDef: {
    flex: 1,
  },
  detailCellRendererParams: {
    detailGridOptions: {
      columnDefs: [...params.columnDefs],
      onGridReady: params.onDetailGridReady,
      rowSelection: 'multiple',
    },
    getDetailRowData: params.getDetailRowData,
    template: AgGridConstants.DEFAULT_DETAIL_CELL_RENDERER_TEMPLATE,
  },
  detailRowAutoHeight: true,
  embedFullWidthRows: true, // make details rows scrollable
  masterDetail: true,
  rowSelection: 'multiple',
});

export const OrderLineItemsAgConfig = {
  COL_DEFS,
  gridOptionsFactory,
};
